import React, { useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { useStoreState, useStoreActions } from "easy-peasy";
import { enterFullScreen } from "../../utils/screen";
import { isMobile } from "../../utils/ResponsiveUtils";

const PlayerControls = ({ mobileResponsiveUtil, content, hasIntro }) => {
  const CONTENT_ID = "interactiveExpPlayerControls";
  const playBtnRef = useRef(null);
  const { homeBtn, playBtn, pauseBtn } = content;
  const { isPlaying: isExperiencePlaying, isFirstPlay } = useStoreState(
    (state) => state.experience
  );
  const { isPlaying, player: videoPlayer } = useStoreState(
    (state) => state.stage
  );
  const { isPlaying: isBgmPlaying, player: bgmPlayer } = useStoreState(
    (state) => state.bgm
  );
  const { isVisible: isNavVisible } = useStoreState(
    (state) => state.navigation
  );
  const demoStartTime = useStoreState((state) => state.demo.startTime);
  const resetExperience = useStoreState(
    (state) => state.experience.resetExperience
  );
  const { setPlaying: setExperiencePlaying, setFirstPlay } = useStoreActions(
    (actions) => actions.experience
  );
  const setPlaying = useStoreActions((actions) => actions.stage.setPlaying);
  const setNavVisibility = useStoreActions(
    (actions) => actions.navigation.setNavVisibility
  );
  const setDemoVisibility = useStoreActions(
    (actions) => actions.demo.setVisible
  );
  const setIntroVisibility = useStoreActions(
    (actions) => actions.intro.setVisible
  );

  const setOutroVisibility = useStoreActions(
    (actions) => actions.outro.setVisible
  );

  const { setPlaying: setBgmPlaying } = useStoreActions(
    (actions) => actions.bgm
  );

  const playCount = useStoreState((state) => state.analytics.playCount);
  const setPlayCount = useStoreActions(
    (actions) => actions.analytics.setPlayCount
  );

  const [preNavVisibility, setPreNavVisibility] = useState(0);
  const handleHomeBtnClick = useCallback(() => {
    setOutroVisibility(false);
    setDemoVisibility(false);
    // reset state
    resetExperience();
    if (videoPlayer && !videoPlayer.paused()) {
      videoPlayer.pause();
    }
    // If Start card
    if (hasIntro) {
      setIntroVisibility(true);
    } else {
      setDemoVisibility(true);
    }
  }, [
    hasIntro,
    resetExperience,
    setDemoVisibility,
    setIntroVisibility,
    setOutroVisibility,
    videoPlayer,
  ]);

  const handlePlayBtnClick = useCallback(() => {
    if (isPlaying) {
      setPreNavVisibility(isNavVisible);
      setNavVisibility(true);
    } else {
      setNavVisibility(preNavVisibility);
    }

    if (videoPlayer) {
      if (!videoPlayer.paused()) {
        videoPlayer.pause();
        // setBgmMuted(true);
        if (bgmPlayer) {
          bgmPlayer.pause();
        }
      } else {
        const playPromise = videoPlayer.play();
        if (playPromise !== "undefined") {
          playPromise
            .then(() => {
              // increase play count
              setPlayCount(playCount + 1);
            })
            .catch((error) => {
              // eslint-disable-next-line no-console
              console.log("Play was cancelled", error);
            });
        }
        if (bgmPlayer) {
          bgmPlayer.play();
        }
      }
    }

    setPlaying(!isPlaying);
    setBgmPlaying(!isBgmPlaying);

    if (!isExperiencePlaying) {
      if (videoPlayer.currentTime() !== demoStartTime) {
        videoPlayer.currentTime(demoStartTime);
      }
      setExperiencePlaying(true);
      // enter full screen
      enterFullScreen();
    }

    if (isFirstPlay) {
      setFirstPlay(false);
    }
  }, [
    videoPlayer,
    setPlaying,
    isPlaying,
    setPreNavVisibility,
    preNavVisibility,
    setNavVisibility,
    isNavVisible,
    isBgmPlaying,
    bgmPlayer,
    isExperiencePlaying,
    setExperiencePlaying,
    demoStartTime,
    setBgmPlaying,
    isFirstPlay,
    setFirstPlay,
    playCount,
    setPlayCount,
  ]);

  return (
    <>
      <div id={CONTENT_ID} className="general-controls js-general-controls ">
        <ul className="general-controls__list">
          {homeBtn && homeBtn.show && (
            <li className="general-controls__list-item general-controls__list-item--home">
              <button
                type="button"
                className="general-controls__btn general-controls__home js-general-controls__home"
                onClick={handleHomeBtnClick}
              >
                <span className="general-controls__btn-icon">
                  <img
                    src={homeBtn && homeBtn.url}
                    alt={(homeBtn && homeBtn.altText) || ""}
                  />
                </span>
              </button>
            </li>
          )}

          <li className="general-controls__list-item general-controls__list-item--play">
            <button
              ref={playBtnRef}
              type="button"
              className="general-controls__btn js-general-controls__play"
              onClick={handlePlayBtnClick}
            >
              {isPlaying ? (
                <span className="general-controls__btn-icon icon-pause">
                  <img
                    src={pauseBtn && pauseBtn.url}
                    alt={pauseBtn && pauseBtn.altText}
                  />
                </span>
              ) : (
                <span className="general-controls__btn-icon icon-play is-visible">
                  <img
                    src={playBtn && playBtn.url}
                    alt={playBtn && playBtn.altText}
                  />
                </span>
              )}
            </button>
          </li>
        </ul>
      </div>
      <style jsx="true">
        {`
          #${CONTENT_ID}.general-controls {
            position: absolute !important;
            z-index: 4 !important;

            ${isMobile()
              ? `
              bottom: ${mobileResponsiveUtil.getHeightValue(100)}px !important;
              left: ${mobileResponsiveUtil.getWidthValue(75)}px !important;
            `
              : `
              bottom: ${mobileResponsiveUtil.getHeightValue(68)}px !important;
              left: ${mobileResponsiveUtil.getWidthValue(75)}px !important;
            `}
          }

          #${CONTENT_ID} .general-controls__list {
            display: flex !important;

            margin: 0 !important;
            padding: 0 !important;

            list-style: none !important;
          }

          #${CONTENT_ID} .general-controls__list-item {
            margin-left: 0 !important;
            margin-right: 0 !important;
            margin-top: 0 !important;
            padding: 0 !important;
          }

          #${CONTENT_ID}
            .general-controls__list-item
            + .general-controls__list-item {
            margin-left: ${mobileResponsiveUtil.getWidthValue(18)}px !important;
          }

          #${CONTENT_ID} .general-controls__btn {
            display: flex !important;

            padding: 0 !important;

            border: 0 !important;
            border-radius: 50% !important;

            align-items: center !important;
            justify-content: center !important;
            box-shadow: none !important;
            background: #ffffff !important;

            ${isMobile()
              ? `
              width: 44px !important;
              height: 44px !important;
            `
              : `
              width: 72px !important;
              height: 72px !important;
            `}
          }
          #${CONTENT_ID} .general-controls__btn:active {
            opacity: 0.5 !important;
          }
          #${CONTENT_ID} .general-controls__btn:hover,
          #${CONTENT_ID} .general-controls__btn:hover * {
            cursor: pointer !important;
          }

          #${CONTENT_ID} .general-controls__btn img,
          #${CONTENT_ID} .general-controls__btn svg {
            display: block !important;

            width: 100% !important;
            height: 100% !important;
          }

          #${CONTENT_ID} .general-controls__btn:focus {
            outline: 0 !important;
          }

          #${CONTENT_ID}
            .general-controls__list-item--play
            .general-controls__btn-icon {
            display: inline-block !important;
            width: 100% !important;
          }

          #${CONTENT_ID} .general-controls__home {
            padding: 0 !important;
          }
        `}
      </style>
    </>
  );
};

PlayerControls.propTypes = {
  mobileResponsiveUtil: PropTypes.shape({
    getHeightValue: PropTypes.func,
    getWidthValue: PropTypes.func,
    getFontSize: PropTypes.func,
  }),
  content: PropTypes.object,
  hasIntro: PropTypes.bool,
  product: PropTypes.shape({
    name: PropTypes.string,
  }),
};

PlayerControls.defaultProps = {
  mobileResponsiveUtil: {
    getHeightValue: () => {},
    getWidthValue: () => {},
    getFontSize: () => {},
  },
  content: {
    homeBtn: {},
    playBtn: {},
    pauseBtn: {},
  },
  product: {
    name: "",
  },
  hasIntro: true,
};

export default PlayerControls;
